
import zxcvbn from 'zxcvbn';

export interface PasswordStrength {
  score: number;
  feedback: {
    warning: string;
    suggestions: string[];
  };
  isStrong: boolean;
}

/**
 * Analyzes password strength using zxcvbn
 * @param password The password to analyze
 * @returns A PasswordStrength object containing score, feedback, and if the password is considered strong
 */
export function validatePassword(password: string): PasswordStrength {
  // Don't analyze empty passwords
  if (!password) {
    return {
      score: 0,
      feedback: {
        warning: "Password is required",
        suggestions: ["Enter a password"]
      },
      isStrong: false
    };
  }

  // Check minimum length first (10+ characters)
  if (password.length < 10) {
    return {
      score: 0,
      feedback: {
        warning: "Password is too short",
        suggestions: ["Use at least 10 characters"]
      },
      isStrong: false
    };
  }

  // Run zxcvbn analysis
  const result = zxcvbn(password);
  
  // Determine if password is strong (score of 3 or 4 is good, and meets length requirement)
  const isStrong = result.score >= 3;

  return {
    score: result.score,
    feedback: result.feedback,
    isStrong
  };
}

/**
 * Get a color based on password strength score
 */
export function getPasswordStrengthColor(score: number): string {
  switch (score) {
    case 0: return 'bg-red-500';
    case 1: return 'bg-orange-500';
    case 2: return 'bg-yellow-500';
    case 3: return 'bg-lime-500';
    case 4: return 'bg-green-500';
    default: return 'bg-gray-300';
  }
}

/**
 * Get a text description based on password strength score
 */
export function getPasswordStrengthText(score: number): string {
  switch (score) {
    case 0: return 'Very Weak';
    case 1: return 'Weak';
    case 2: return 'Fair';
    case 3: return 'Good';
    case 4: return 'Strong';
    default: return 'Unknown';
  }
}
